package org.teddapp.project.db

import com.tedd.db.Pomodoro_item
import com.tedd.db.Pomodoro_session
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import org.teddapp.project.db.repo.PomodoroItemRepository
import org.teddapp.project.db.repo.PomodoroSessionRepository

/**
 * Orchestrateur Pomodoro (spec POMODORO §4, §8.2, §8.4, §8.5, §8.8, §9.2).
 *
 * Responsabilités :
 *  - Récupère ou crée la session du jour, avec réouverture implicite avant mutation.
 *  - Ajoute un item (avec doublon-check) depuis une source externe ou en quick.
 *  - Bascule la complétion d'un item ET propage vers la source :
 *      - "action"        → ProgressService (toggleTask)
 *      - "project_task"  → avance / recule d'une colonne Kanban
 *      - "quick" / "exercise" / "exercise_task" → pas de propagation
 *  - Clone une session passée vers aujourd'hui en ignorant les sources mortes.
 */
class PomodoroService(
    private val db: TeddDatabase,
    private val sessions: PomodoroSessionRepository,
    private val items: PomodoroItemRepository,
    private val progress: ProgressService,
) {

    /** Session du jour (étendue ou exact-date), créée à la volée si absente. */
    suspend fun openTodaySession(today: String): Pomodoro_session =
        sessions.getOrCreateToday(today)

    /**
     * Réouverture implicite : si la session est done, on la passe Active avant toute mutation.
     */
    private suspend fun ensureActive(sessionId: String) {
        val s = db.pomodoroSessionQueries.selectById(sessionId).executeAsOneOrNull() ?: return
        if (s.status == "done") sessions.reopen(sessionId)
    }

    /**
     * Ajout depuis une source. Retourne :
     *   - id de l'item créé
     *   - null si la source était déjà présente (doublon — spec §9.1)
     */
    suspend fun addFromSource(
        today: String,
        sourceType: String,
        sourceId: String,
        priority: Long = 4,
    ): String? {
        val session = sessions.getOrCreateToday(today)
        ensureActive(session.id)
        return items.addFromSource(session.id, sourceType, sourceId, priority)
    }

    /** Ajout quick task. */
    suspend fun addQuick(today: String, title: String, priority: Long = 4, estimatedMinutes: Long? = null): String {
        val session = sessions.getOrCreateToday(today)
        ensureActive(session.id)
        return items.addQuick(session.id, title, priority, estimatedMinutes)
    }

    /**
     * Bascule la complétion + propage (spec §8.4).
     * Retourne la nouvelle valeur completed.
     */
    suspend fun toggleItem(itemId: String): Boolean = withContext(Dispatchers.Default) {
        val item = items.byId(itemId) ?: return@withContext false
        val nowCompleted = items.toggleCompletedRaw(itemId)
        propagate(item, nowCompleted)
        nowCompleted
    }

    /**
     * Propagation manuelle (utile pour cohérence sans toggle, ex: après clonage).
     * Ne change PAS l'état completed dans pomodoro_item — appelle uniquement la source.
     */
    private suspend fun propagate(item: Pomodoro_item, completed: Boolean) {
        when (item.source_type) {
            "action" -> propagateToAction(item.source_id, completed)
            "project_task" -> propagateToProjectTask(item.source_id, completed)
            "quick", "exercise", "exercise_task" -> Unit
            else -> Unit
        }
    }

    /** Propage vers une action (objectif) en réutilisant ProgressService. */
    private suspend fun propagateToAction(taskId: String, completed: Boolean) {
        val task = db.taskQueries.selectById(taskId).executeAsOneOrNull() ?: return
        val targetStatus = if (completed) "done" else "pending"
        if (task.status != targetStatus) {
            progress.setTaskStatus(task, targetStatus)
        }
    }

    /**
     * Propage vers une tâche projet : avance d'une colonne Kanban si completed,
     * recule sinon (spec §8.5). No-op si déjà en bout de chaîne dans le sens demandé.
     */
    private suspend fun propagateToProjectTask(taskId: String, completed: Boolean) {
        val task = db.taskQueries.selectById(taskId).executeAsOneOrNull() ?: return
        val currentStatusId = task.status_id ?: return
        val currentStatus = db.projectStatusQueries.selectById(currentStatusId).executeAsOneOrNull() ?: return
        val projectId = currentStatus.project_id
        val statuses = db.projectStatusQueries.selectByProject(projectId).executeAsList()
        val target = if (completed) {
            statuses.firstOrNull { it.order_index > currentStatus.order_index }
        } else {
            statuses.lastOrNull { it.order_index < currentStatus.order_index }
        } ?: return
        db.taskQueries.updateProjectStatus(target.id, task.order_index, nowMillis(), task.id)
    }

    /** Clôt la session du jour (spec §8.11). */
    suspend fun terminateToday(today: String) {
        val s = sessions.getOrCreateToday(today)
        sessions.terminate(s.id)
    }

    /** Étend ou annule l'extension (spec §8.9). */
    suspend fun setExtension(today: String, endDate: String?) {
        val s = sessions.getOrCreateToday(today)
        sessions.setDateEnd(s.id, endDate)
    }

    /**
     * Clonage d'une session passée (spec §8.8).
     * Retourne le nombre d'items effectivement copiés.
     * Skip si :
     *   - source externe introuvable (action / project_task supprimée)
     *   - doublon dans la session du jour (sauf quick)
     */
    suspend fun cloneSession(today: String, sourceSessionId: String): Int =
        withContext(Dispatchers.Default) {
            val target = sessions.getOrCreateToday(today)
            ensureActive(target.id)
            val srcItems = db.pomodoroItemQueries
                .selectActiveItemsBySession(sourceSessionId)
                .executeAsList()
            var cloned = 0
            for (src in srcItems) {
                val sourceExists = when (src.source_type) {
                    "action", "project_task" -> db.taskQueries.selectById(src.source_id).executeAsOneOrNull() != null
                    "quick" -> true
                    "exercise", "exercise_task" -> true  // pas de jointure prévue — on copie
                    else -> false
                }
                if (!sourceExists) continue

                val newId = if (src.source_type == "quick") {
                    // Toujours OK, génère un nouveau quick id.
                    items.addQuick(target.id, src.custom_title ?: "Tâche rapide", src.priority, src.estimated_minutes)
                } else {
                    items.addFromSource(target.id, src.source_type, src.source_id, src.priority)
                }
                if (newId != null) {
                    copyChecks(src.id, newId)
                    cloned += 1
                }
            }
            cloned
        }

    /**
     * Les vérifications suivent l'item cloné : mêmes critères, même ordre, décochés
     * (c'est un nouveau passage). Elles appartiennent à un item ; sans cette copie, le
     * clone repartait sans elles et elles ne restaient visibles que sur la session d'origine.
     */
    private fun copyChecks(fromItemId: String, toItemId: String) {
        val now = nowMillis()
        db.transaction {
            db.pomodoroCheckQueries.selectByItem(fromItemId).executeAsList().forEach { c ->
                db.pomodoroCheckQueries.insert(newId("pc"), toItemId, c.label, c.note, 0L, c.order_index, now, now)
            }
        }
    }
}
